import { useState, type FormEvent } from "react";
import SectionHeader from "./SectionHeader";

const channels = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@xukong-manhwa.com", href: "mailto:contact@xukong-manhwa.com" },
  { icon: "ri-wechat-line", label: "官方微信", value: "xukong_manhwa", href: "#contact" },
  { icon: "ri-telegram-line", label: "粉丝社群", value: "t.me/xukongmanhwa", href: "#contact" },
];

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("website_alt")?.toString().trim() ?? "";

    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const payload = new FormData(form);
    payload.delete("website_alt");
    setSending(true);
    setFormStatus("idle");
    setFormError("");

    try {
      const res = await fetch("https://readdy.ai/api/form/da24f85v7gurs51ef6d0", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: payload,
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const success =
        res.ok && parsed?.code === "OK" && !/spam/i.test(responseText) && !/spam/i.test(serverMsg || "");
      if (success) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，请稍后再试。");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          tag="联系我们"
          title="追更建议，随时告诉我们"
          desc="对追更进度、连载提醒或内容推荐有任何想法，欢迎留下你的反馈，我们会认真阅读每一条建议。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">联系方式</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              无论是作品推荐、进度勘误，还是功能建议，都可以通过以下方式与我们取得联系。
            </p>
            <ul className="mt-6 flex flex-col gap-4">
              {channels.map((c) => (
                <li key={c.label}>
                  <a
                    href={c.href}
                    className="flex items-center gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-200"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <div>
                      <div className="text-xs text-foreground-500">{c.label}</div>
                      <div className="mt-0.5 text-sm font-semibold text-foreground-950">{c.value}</div>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <form
            data-readdy-form="contact"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground-800">
                称呼
                <input
                  type="text"
                  name="name"
                  placeholder="怎么称呼你"
                  className="h-11 rounded-lg border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>
              <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground-800">
                联系邮箱
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="h-11 rounded-lg border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>
            </div>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground-800">
              反馈类型
              <select
                name="topic"
                className="h-11 rounded-lg border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
              >
                <option value="作品推荐">作品推荐</option>
                <option value="进度勘误">进度勘误</option>
                <option value="功能建议">功能建议</option>
                <option value="其他问题">其他问题</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground-800">
              留言内容
              <textarea
                name="message"
                required
                rows={4}
                maxLength={500}
                placeholder="请告诉我们你的想法（500 字以内）"
                className="resize-none rounded-lg border border-background-200 bg-background-50 px-3 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              ></textarea>
            </label>

            <div className="honeypot-field">
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {sending ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  提交中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-base"></i>
                  提交反馈
                </>
              )}
            </button>

            {formStatus === "success" && (
              <p className="rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                感谢你的反馈！我们会尽快查看你的留言。
              </p>
            )}
            {formStatus === "error" && (
              <p className="rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800">{formError}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}